import type { HiredEquipmentAnchor, HiredEquipmentGridRow } from '../types';
import { LUMP_SUM_RESOURCE_LABEL, LUMP_SUM_UNIT } from '../types';
import { formatTaskHead, todayIsoDate } from './poPrefill';
import {
  formatHeOvertimeBreakdownText,
  isHoursUnit,
  parseOvertimeEligibleFlag,
} from './overtime';
import { lineAmount } from './gridMath';
import {
  formatHeWorkDate,
  heEntryMethodFromForm,
  heVendorNameFromForm,
  heWorkDateFromForm,
} from './approvalListRow';

export interface HeSummaryField {
  field: string;
  path?: string;
  value?: string | null;
}

export interface HeSummaryTaskRef {
  id: number;
  taskNumber?: string | null;
  name?: string | null;
  description?: string | null;
  taskDescription?: string | null;
  projectName?: string | null;
}

export interface HeSummaryRow {
  key: string;
  dateLabel: string;
  ticket: string;
  taskLabel: string;
  vendor: string;
  po: string;
  resource: string;
  qty: number | null;
  rate: number | null;
  fuelPriceAdjustment: number | null;
  value: number;
  unit: string;
  overtimeEligible: boolean;
  otBreakdown: string | null;
  /** Flat-amount line: qty and rate are placeholders, only `value` is meaningful. */
  lumpSum: boolean;
}

/** WRAP FormField marking a grid line as a flat-amount lump sum. */
export const LUMP_SUM_FIELD = 'LumpSum';

function parseLumpSumFlag(raw: string): boolean {
  const value = raw.trim().toLowerCase();
  return value === 'true' || value === 'yes' || value === '1';
}

export function hasHePaperTicket(value: string | null | undefined): boolean {
  return Boolean((value ?? '').trim());
}

/** WRAP HE ticket, or TrueContext HE paths that already persist EquipmentTimeWorkedJ. */
export function formUsesHeLineItemSummary(fields: HeSummaryField[]): boolean {
  return fields.some(
    (f) => (f.path || '').includes('EquipmentTimeWorkedJ') || f.field === 'HiredEquipmentTicket',
  );
}

export function formatHeSummaryQty(qty: number | null): string {
  if (qty == null || Number.isNaN(qty)) return '—';
  if (Number.isInteger(qty)) return String(qty);
  return qty.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

export function sumHeSummaryValues(rows: HeSummaryRow[]): number {
  return rows.reduce((sum, row) => sum + row.value, 0);
}

function parseNumber(raw: string | undefined | null): number | null {
  if (raw == null || String(raw).trim() === '') return null;
  const n = Number(String(raw).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function parseVendorMaterial(raw: string): boolean {
  const value = raw.trim().toLowerCase();
  return value === 'true' || value === 'yes' || value === '1';
}

function parseFuelSurchargeEligible(raw: string): boolean | null {
  const value = raw.trim().toLowerCase();
  if (value === 'true' || value === 'yes' || value === '1') return true;
  if (value === 'false' || value === 'no' || value === '0') return false;
  return null;
}

function fieldValue(fields: HeSummaryField[], name: string): string {
  return fields.find((f) => f.field === name)?.value?.trim() || '';
}

function compareDottedIds(a: string, b: string): number {
  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i += 1) {
    const diff = (aParts[i] || 0) - (bParts[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function groupByTaskSection(fields: HeSummaryField[]): Map<string, HeSummaryField[]> {
  const map = new Map<string, HeSummaryField[]>();
  for (const field of fields) {
    const match = (field.path || '').match(/WorkCompleted\.Tasks (\d+\.\d+)/);
    if (!match) continue;
    const list = map.get(match[1]) ?? [];
    list.push(field);
    map.set(match[1], list);
  }
  return map;
}

function groupByEquipment(fields: HeSummaryField[]): Map<string, HeSummaryField[]> {
  const map = new Map<string, HeSummaryField[]>();
  for (const field of fields) {
    const match = (field.path || '').match(/TimeWorkedJobCost 1\.(\d+)/);
    if (!match) continue;
    const list = map.get(match[1]) ?? [];
    list.push(field);
    map.set(match[1], list);
  }
  return map;
}

function setupPoNumber(fields: HeSummaryField[]): string {
  return (
    fields.find((f) => f.field === 'PONumber' && !(f.path || '').includes('WorkCompleted'))?.value?.trim() ||
    ''
  );
}

function resolveTaskLabel(rawTaskId: string, tasks: HeSummaryTaskRef[]): string {
  const taskId = Number.parseInt(rawTaskId, 10);
  if (!Number.isFinite(taskId) || taskId <= 0) return rawTaskId || '—';
  const task = tasks.find((t) => t.id === taskId);
  if (!task) return rawTaskId;
  return formatTaskHead(task);
}

function resolveRate(equipmentFields: HeSummaryField[]): number | null {
  const original = parseNumber(fieldValue(equipmentFields, 'HiredEquipmentRate'));
  if (original != null) return original;
  return (
    parseNumber(fieldValue(equipmentFields, 'RateBlueBook')) ??
    parseNumber(fieldValue(equipmentFields, 'RateBlueBookPrim')) ??
    parseNumber(fieldValue(equipmentFields, 'RateBlueBookSec'))
  );
}

function resolveValue(
  equipmentFields: HeSummaryField[],
  qty: number | null,
  rate: number | null,
  overtimeEligible: boolean,
  unit: string,
): number {
  const fuelAdj = parseNumber(fieldValue(equipmentFields, 'FuelPriceAdjustment')) ?? 0;
  if (qty != null && rate != null) {
    return lineAmount(qty, rate, overtimeEligible, unit, fuelAdj);
  }
  return (
    parseNumber(fieldValue(equipmentFields, 'AmountBilled')) ??
    parseNumber(fieldValue(equipmentFields, 'CalculatedCost')) ??
    0
  );
}

/**
 * One read-only approval row per HE equipment group (WRAP grid line, or TrueContext HE line).
 */
export function parseHeSummaryRows(fields: HeSummaryField[], tasks: HeSummaryTaskRef[] = []): HeSummaryRow[] {
  const setupVendor = heVendorNameFromForm(fields);
  const setupPo = setupPoNumber(fields);
  const setupDate = heWorkDateFromForm(fields);
  const rows: HeSummaryRow[] = [];

  const taskGroups = groupByTaskSection(fields);
  const groupIds = Array.from(taskGroups.keys()).sort(compareDottedIds);

  for (const groupId of groupIds) {
    const groupFields = taskGroups.get(groupId) || [];
    const dateRaw =
      groupFields.find((f) => f.field === 'ActualStartTime')?.value?.trim() || setupDate;
    const taskRaw =
      groupFields.find((f) => f.field === 'Task' && (f.path || '').endsWith('.Task'))?.value?.trim() ||
      fieldValue(groupFields, 'TaskId') ||
      fieldValue(groupFields, 'Task');
    const equipmentGroups = groupByEquipment(groupFields);
    const equipmentIds = Array.from(equipmentGroups.keys()).sort(compareDottedIds);
    const ids = equipmentIds.length > 0 ? equipmentIds : ['0'];

    for (const equipmentId of ids) {
      const equipmentFields = equipmentGroups.get(equipmentId) || groupFields;
      const lumpSum = parseLumpSumFlag(fieldValue(equipmentFields, LUMP_SUM_FIELD));
      const qty = parseNumber(fieldValue(equipmentFields, 'HoursLabour'));
      const rate = resolveRate(equipmentFields);
      const unit = fieldValue(equipmentFields, 'UnitsMeasurement') || 'hr';
      const overtimeEligible = parseOvertimeEligibleFlag(fieldValue(equipmentFields, 'OvertimeEligible'));
      const fuelPriceAdjustment = parseNumber(fieldValue(equipmentFields, 'FuelPriceAdjustment'));
      const value = resolveValue(equipmentFields, qty, rate, overtimeEligible, unit);
      const vendor = fieldValue(equipmentFields, 'SubcontractorNameCom') || setupVendor;
      const resource = fieldValue(equipmentFields, 'SubcontractorName');
      const po = fieldValue(equipmentFields, 'PONumber') || setupPo;

      const hasLine =
        qty != null ||
        rate != null ||
        value > 0 ||
        Boolean(resource) ||
        Boolean(vendor && po);
      if (!hasLine) continue;

      rows.push({
        key: `${groupId}.${equipmentId}`,
        dateLabel: formatHeWorkDate(dateRaw) || '—',
        ticket: fieldValue(equipmentFields, 'TicketNumber'),
        taskLabel: resolveTaskLabel(taskRaw, tasks),
        vendor: vendor || '—',
        po: po || '—',
        resource: resource || '—',
        qty,
        rate,
        fuelPriceAdjustment,
        value,
        unit,
        overtimeEligible,
        lumpSum,
        otBreakdown: formatHeOvertimeBreakdownText({
          qty,
          rate,
          overtimeEligible,
          unit,
          fuelPerHour: fuelPriceAdjustment ?? 0,
        }),
      });
    }
  }

  return rows;
}

export function toIsoDate(raw: string | null | undefined): string {
  const match = (raw || '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

export function heAnchorFromForm(fields: HeSummaryField[]): HiredEquipmentAnchor {
  const entryMethod = heEntryMethodFromForm(fields) ?? 'task';
  const vendor = heVendorNameFromForm(fields);
  const po = setupPoNumber(fields);
  const anchor: HiredEquipmentAnchor = { entryMethod };
  if ((entryMethod === 'purchase_order' || entryMethod === 'subcontractor') && vendor) {
    anchor.lockedVendorName = vendor;
  }
  if (entryMethod === 'purchase_order' && po) {
    anchor.lockedPoNumber = po;
  }
  return anchor;
}

export function parseHeGridRows(
  fields: HeSummaryField[],
  tasks: HeSummaryTaskRef[] = [],
): HiredEquipmentGridRow[] {
  const setupVendor = heVendorNameFromForm(fields);
  const setupPo = setupPoNumber(fields);
  const setupDate = toIsoDate(heWorkDateFromForm(fields)) || todayIsoDate();
  const rows: HiredEquipmentGridRow[] = [];

  const taskGroups = groupByTaskSection(fields);
  const groupIds = Array.from(taskGroups.keys()).sort(compareDottedIds);

  for (const groupId of groupIds) {
    const groupFields = taskGroups.get(groupId) || [];
    const dateRaw =
      toIsoDate(groupFields.find((f) => f.field === 'ActualStartTime')?.value) || setupDate;
    const taskRaw =
      groupFields.find((f) => f.field === 'Task' && (f.path || '').endsWith('.Task'))?.value?.trim() ||
      fieldValue(groupFields, 'TaskId') ||
      fieldValue(groupFields, 'Task');
    const taskId = Number.parseInt(taskRaw, 10);
    const equipmentGroups = groupByEquipment(groupFields);
    const equipmentIds = Array.from(equipmentGroups.keys()).sort(compareDottedIds);
    const ids = equipmentIds.length > 0 ? equipmentIds : ['0'];

    for (const equipmentId of ids) {
      const equipmentFields = equipmentGroups.get(equipmentId) || groupFields;
      const lumpSum = parseLumpSumFlag(fieldValue(equipmentFields, LUMP_SUM_FIELD));
      const qty = parseNumber(fieldValue(equipmentFields, 'HoursLabour'));
      const rate = resolveRate(equipmentFields);
      const vendor = fieldValue(equipmentFields, 'SubcontractorNameCom') || setupVendor;
      const resource = fieldValue(equipmentFields, 'SubcontractorName');
      const po = fieldValue(equipmentFields, 'PONumber') || setupPo;
      const unit = fieldValue(equipmentFields, 'UnitsMeasurement') || 'hr';
      const fuelRaw = fieldValue(equipmentFields, 'FuelSurchargeEligible');
      const fuelSurchargeEligible = fuelRaw ? parseFuelSurchargeEligible(fuelRaw) : null;
      const vendorMaterial = parseVendorMaterial(fieldValue(equipmentFields, 'VendorMaterial'));
      const vendorMaterialNotes =
        fieldValue(equipmentFields, 'VendorMaterialNotes') ||
        fieldValue(equipmentFields, 'Comments 5');
      const vehicleType = fieldValue(equipmentFields, 'PrimaryVehicleType') || null;
      const overtimeRaw = fieldValue(equipmentFields, 'OvertimeEligible');
      const overtimeEligible = overtimeRaw ? parseOvertimeEligibleFlag(overtimeRaw) : false;
      const resourceOvertimeEligible = overtimeEligible
        ? true
        : isHoursUnit(unit)
          ? null
          : false;

      const hasLine =
        qty != null ||
        rate != null ||
        Boolean(resource) ||
        Boolean(vendor && po) ||
        Boolean(taskRaw);
      if (!hasLine) continue;

      rows.push({
        clientId: `he-form-${groupId}-${equipmentId}`,
        taskGroupId: groupId,
        lumpSum,
        date: dateRaw,
        vendorName: vendor || null,
        poNumber: po || null,
        taskId: Number.isFinite(taskId) && taskId > 0 ? taskId : null,
        taskLabel: resolveTaskLabel(taskRaw, tasks),
        resourceLabel: lumpSum ? LUMP_SUM_RESOURCE_LABEL : resource || null,
        resourceId: lumpSum
          ? null
          : (() => {
              const raw = fieldValue(equipmentFields, 'ResourceId');
              const id = Number.parseInt(raw, 10);
              return Number.isFinite(id) && id > 0 ? id : null;
            })(),
        qty: lumpSum ? 1 : qty,
        unit: lumpSum ? LUMP_SUM_UNIT : unit,
        rate,
        vendorMaterial,
        vendorMaterialNotes,
        fuelSurchargeEligible: lumpSum ? false : fuelSurchargeEligible,
        overtimeEligible: lumpSum ? false : overtimeEligible,
        resourceOvertimeEligible: lumpSum ? false : resourceOvertimeEligible,
        vehicleType,
        ticketNumber: fieldValue(equipmentFields, 'TicketNumber') || null,
        fuelSurchargeOverridePerHour: parseNumber(fieldValue(equipmentFields, 'FuelSurchargeOverridePerHour')),
      });
    }
  }

  return rows;
}

export function heGridPersistPayload(rows: HiredEquipmentGridRow[]) {
  return rows.map((row) => ({
    lumpSum: row.lumpSum,
    date: row.date,
    taskId: row.taskId,
    vendorName: row.vendorName,
    resourceLabel: row.resourceLabel,
    resourceId: row.resourceId ?? null,
    qty: row.qty,
    unit: row.unit,
    rate: row.rate,
    poNumber: row.poNumber,
    fuelSurchargeEligible: row.fuelSurchargeEligible,
    overtimeEligible: row.overtimeEligible,
    vendorMaterial: row.vendorMaterial,
    vendorMaterialNotes: row.vendorMaterialNotes ?? '',
    vehicleType: row.vehicleType ?? null,
    ticketNumber: row.ticketNumber?.trim() || null,
    fuelSurchargeOverridePerHour: row.fuelSurchargeOverridePerHour ?? null,
  }));
}

export function heGridSubmitRows(rows: HiredEquipmentGridRow[]) {
  return rows.map((row) => ({
    lumpSum: row.lumpSum,
    date: row.date,
    taskId: row.taskId as number,
    vendorId: row.vendorId,
    vendorName: row.vendorName,
    resourceLabel: row.resourceLabel,
    resourceId: row.resourceId ?? null,
    qty: row.qty as number,
    unit: row.unit,
    rate: row.rate as number,
    poNumber: row.poNumber,
    fuelSurchargeEligible: row.fuelSurchargeEligible,
    overtimeEligible: row.overtimeEligible,
    vendorMaterial: row.vendorMaterial,
    vendorMaterialNotes: row.vendorMaterialNotes || null,
    vehicleType: row.vehicleType ?? null,
    ticketNumber: row.ticketNumber?.trim() || null,
    fuelSurchargeOverridePerHour: row.fuelSurchargeOverridePerHour ?? null,
    // Tells the server this submission owns the task, so approval may close it.
    taskCreatedInSession: row.taskCreatedInSession === true,
    attachment: row.attachment
      ? {
          filename: row.attachment.filename,
          contentType: row.attachment.contentType,
          size: row.attachment.size,
          url: row.attachment.url,
          key: row.attachment.key,
        }
      : null,
  }));
}
